import {
  CLOCK_TIME_FIELD_MAX,
  DEFAULT_TIME_INPUT_MAX_LENGTH,
  TIME_DURATION_FIELD,
  type TimeField
} from 'constants/time-duration';
import { t } from 'services/i18n.service';

export type { TimeField };

export interface TimeInputValidationResult {
  cleanedValue: string;
  limitedValue: string;
  isValid: boolean;
  errorMessage: string | null;
  timeValue: {
    hours: number;
    minutes: number;
    seconds: number;
  } | null;
}

export type TimeInputLimits = {
  maxMinutes?: number;
};

export const sanitizeTimeInput = (value: string): string => {
  return value.replace(/[^0-9]+/g, '');
};

export const getTimeFieldMaxValue = (
  field: TimeField,
  maxMinutes: number = CLOCK_TIME_FIELD_MAX.MINUTES
): number => {
  switch (field) {
    case TIME_DURATION_FIELD.HOURS:
      return CLOCK_TIME_FIELD_MAX.HOURS;
    case TIME_DURATION_FIELD.MINUTES:
      return maxMinutes;
    case TIME_DURATION_FIELD.SECONDS:
      return CLOCK_TIME_FIELD_MAX.SECONDS;
    default:
      return CLOCK_TIME_FIELD_MAX.SECONDS;
  }
};

export const getTimeFieldMaxLength = (
  field: TimeField,
  maxMinutes: number = CLOCK_TIME_FIELD_MAX.MINUTES
): number => {
  if (field === TIME_DURATION_FIELD.MINUTES) {
    return String(maxMinutes).length;
  }
  return DEFAULT_TIME_INPUT_MAX_LENGTH;
};

const buildTimeValue = (
  field: TimeField,
  numValue: number,
  currentHours: number,
  currentMinutes: number,
  currentSeconds: number
) => ({
  hours: field === TIME_DURATION_FIELD.HOURS ? numValue : currentHours,
  minutes: field === TIME_DURATION_FIELD.MINUTES ? numValue : currentMinutes,
  seconds: field === TIME_DURATION_FIELD.SECONDS ? numValue : currentSeconds
});

const resolveErrorMessage = (
  field: TimeField,
  max: number,
  isEmpty: boolean,
  getErrorMessage?: (max: number, field: string) => string
): string => {
  if (isEmpty) {
    return t('time_input.required_error', {
      field: t(`time_input.${field}`)
    });
  }
  if (getErrorMessage) {
    return getErrorMessage(max, field);
  }
  return t('time_input.max_value_error', {
    max,
    field: t(`time_input.${field}`)
  });
};

export const validateTimeInput = (
  field: TimeField,
  newValue: string,
  currentHours: number,
  currentMinutes: number,
  currentSeconds: number,
  getErrorMessage?: (max: number, field: string) => string,
  limits?: TimeInputLimits
): TimeInputValidationResult => {
  const maxMinutes = limits?.maxMinutes ?? CLOCK_TIME_FIELD_MAX.MINUTES;
  const maxLength = getTimeFieldMaxLength(field, maxMinutes);
  const cleaned = sanitizeTimeInput(newValue);
  const limited =
    cleaned.length > maxLength ? cleaned.substring(0, maxLength) : cleaned;
  const numValue = parseInt(limited, 10);
  const max = getTimeFieldMaxValue(field, maxMinutes);
  const isEmpty = limited.length === 0;
  const isNumericValid = !isNaN(numValue) && numValue <= max;
  const isValid = !isEmpty && isNumericValid;

  return {
    cleanedValue: cleaned,
    limitedValue: limited,
    isValid,
    errorMessage: isValid
      ? null
      : resolveErrorMessage(field, max, isEmpty, getErrorMessage),
    timeValue: isValid
      ? buildTimeValue(
          field,
          numValue,
          currentHours,
          currentMinutes,
          currentSeconds
        )
      : null
  };
};
